import React, { useCallback, useEffect, useState } from "react";

import AdminNavBar from "../components/AdminNavBar";
import Toast from "../components/Toast";
import { useConfirmDialog } from "../components/ConfirmDialogProvider";
import {
  AdminEmptyState,
  AdminLoadingState,
  AdminPageBody,
  AdminSectionHeader,
  AdminTablePanel,
  adminButton,
  adminPrimaryButton,
} from "../components/AdminPageShell";
import { useAdminCategories } from "../store/admin";
import { formatDate } from "./components/invoices/invoiceUtils";

const EMPTY_FORM = { name: "", description: "", image: "" };

const Categories = () => {
  const confirm = useConfirmDialog();
  const {
    categories,
    categoriesLoading,
    categoriesError,
    categoryMutationLoading,
    fetchCategories,
    fetchCategory,
    createCategory,
    updateCategory,
    deleteCategory,
  } = useAdminCategories();

  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [toast, setToast] = useState(null);
  const [isSuccess, setIsSuccess] = useState(true);

  const showToast = useCallback((message, success = true) => {
    setToast(message);
    setIsSuccess(success);
    setTimeout(() => setToast(null), 5000);
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = async (category) => {
    setEditingId(category.id);
    setForm({
      name: category.name || "",
      description: category.description || "",
      image: category.image || "",
    });
    setShowForm(true);
    const result = await fetchCategory(category.id);
    if (result.success && result.category) {
      setForm({
        name: result.category.name || "",
        description: result.category.description || "",
        image: result.category.image || "",
      });
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const payload = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      image: form.image.trim() || null,
    };
    if (!payload.name) {
      showToast("Category name is required", false);
      return;
    }
    const result = editingId
      ? await updateCategory(editingId, payload)
      : await createCategory(payload);
    if (!result.success) {
      showToast(result.error || "Failed to save category", false);
      return;
    }
    showToast(editingId ? "Category updated" : "Category created", true);
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    fetchCategories();
  };

  const handleDelete = (category) =>
    confirm({
      title: `Delete "${category.name}"?`,
      description:
        "This removes the category. Deletion is rejected while it still has courses.",
      confirmLabel: "Delete category",
      onConfirm: async () => {
        const result = await deleteCategory(category.id);
        if (!result.success) throw new Error(result.error || "Failed to delete category.");
        showToast("Category deleted", true);
        await fetchCategories();
      },
    });

  return (
    <div>
      <AdminNavBar title="Course Categories" />
      <AdminPageBody>
        {toast && <Toast message={toast} onClose={() => setToast(null)} isSuccess={isSuccess} />}

        <AdminSectionHeader
          title="Course Categories"
          description="Create, rename and remove the categories learners browse by."
          count={`${categories.length} categories`}
          actions={
            <button className={adminPrimaryButton} onClick={openCreate}>
              Add Category
            </button>
          }
        />

        {showForm && (
          <form
            onSubmit={handleSubmit}
            className="grid gap-4 rounded-lg border border-slate-200 bg-white p-4 shadow-sm md:grid-cols-3"
          >
            <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Name
              <input
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
                className="h-10 rounded-lg border border-slate-300 px-3 text-sm font-normal normal-case tracking-normal"
                required
              />
            </label>
            <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Image URL
              <input
                value={form.image}
                onChange={(event) => setForm({ ...form, image: event.target.value })}
                className="h-10 rounded-lg border border-slate-300 px-3 text-sm font-normal normal-case tracking-normal"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500 md:col-span-3">
              Description
              <textarea
                value={form.description}
                onChange={(event) => setForm({ ...form, description: event.target.value })}
                rows={3}
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal normal-case tracking-normal"
              />
            </label>
            <div className="flex gap-2 md:col-span-3">
              <button type="submit" className={adminPrimaryButton} disabled={categoryMutationLoading}>
                {categoryMutationLoading ? "Saving…" : editingId ? "Save Changes" : "Create Category"}
              </button>
              <button
                type="button"
                className={adminButton}
                onClick={() => {
                  setShowForm(false);
                  setEditingId(null);
                  setForm(EMPTY_FORM);
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {categoriesError && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600">
            {categoriesError}
          </div>
        )}

        {categoriesLoading ? (
          <AdminLoadingState />
        ) : categories.length === 0 ? (
          <AdminEmptyState title="No categories yet" description="Create the first course category." />
        ) : (
          <AdminTablePanel>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="px-4 py-2 text-xs font-semibold uppercase text-slate-600">Name</th>
                    <th className="px-4 py-2 text-xs font-semibold uppercase text-slate-600">
                      Description
                    </th>
                    <th className="px-4 py-2 text-xs font-semibold uppercase text-slate-600">
                      Courses
                    </th>
                    <th className="px-4 py-2 text-xs font-semibold uppercase text-slate-600">
                      Created
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-semibold uppercase text-slate-600">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map((category) => (
                    <tr key={category.id} className="border-b border-slate-100">
                      <td className="px-4 py-2 text-sm font-medium text-slate-800">
                        {category.name}
                      </td>
                      <td className="max-w-[320px] truncate px-4 py-2 text-sm text-slate-600">
                        {category.description || "—"}
                      </td>
                      <td className="px-4 py-2 text-sm">{category.courseCount ?? 0}</td>
                      <td className="px-4 py-2 text-xs text-slate-500">
                        {formatDate(category.createdAt)}
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex justify-end gap-3">
                          <button
                            className="text-xs font-semibold text-blue-700 hover:text-blue-900"
                            onClick={() => openEdit(category)}
                          >
                            Edit
                          </button>
                          <button
                            className="text-xs font-semibold text-red-600 hover:text-red-800"
                            onClick={() => handleDelete(category)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </AdminTablePanel>
        )}
      </AdminPageBody>
    </div>
  );
};

export default Categories;
